import { PageLayout } from '@/components/feature/PageLayout';
import { useFadeUp } from '@/hooks/useFadeUp';
import { MapPin, Phone } from 'lucide-react';
import { communes } from '@/mocks/communes';

const seo = {
  title: 'Zones d\'intervention — Réparation volets roulants Charleroi & Hainaut',
  description: 'Nous intervenons dans tout le Hainaut pour la réparation et le dépannage de vos volets roulants. Charleroi, Châtelet, Courcelles, La Louvière, Thuin, Binche et toutes les communes environnantes.',
  keywords: 'zones intervention volet roulant Hainaut, réparation volet Charleroi, dépannage volet toutes communes, artisan volet roulant région Charleroi',
  canonicalUrl: '/zones-dinterventions/',
};

function Hero() {
  return (
    <section className="relative min-h-[350px] md:min-h-[420px] flex items-center overflow-hidden">
      <img
        src="/images/hero-zones-intervention.webp"
        alt="Zones d'intervention dans le Hainaut"
        className="absolute inset-0 w-full h-full object-cover object-top"
        loading="eager"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-primary-500/85 to-secondary-500/70" />
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-20">
        <div className="max-w-2xl">
          <h1 className="text-3xl md:text-5xl font-heading font-bold text-white leading-tight">
            Zones d'intervention
          </h1>
          <p className="mt-4 text-base md:text-lg text-white/85">
            Nous intervenons à Charleroi et dans toutes les communes du Hainaut
          </p>
        </div>
      </div>
    </section>
  );
}

export default function ZonesInterventionPage() {
  const ref1 = useFadeUp();

  return (
    <PageLayout seo={seo}>
      <Hero />

      <section className="py-14 md:py-20 bg-background-50">
        <div ref={ref1} className="fade-up max-w-5xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <h2 className="section-title">26 communes couvertes dans le Hainaut</h2>
            <p className="section-subtitle">
              Basés à Charleroi, nous nous déplaçons dans toutes ces communes pour réparer vos volets roulants
            </p>
          </div>

          {/* Charleroi highlight */}
          <div className="card border border-background-200 mb-10 text-center p-6 md:p-8 bg-primary-50 border-primary-100">
            <div className="w-14 h-14 rounded-2xl bg-accent-500 flex items-center justify-center mx-auto mb-4">
              <MapPin className="w-7 h-7 text-white" />
            </div>
            <h3 className="font-heading font-bold text-foreground-700 text-xl mb-2">Charleroi (6000)</h3>
            <p className="text-sm text-foreground-500">
              Notre zone d'intervention principale. Intervention rapide dans tous les quartiers de Charleroi et son agglomération.
            </p>
          </div>

          {/* Commune grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {communes.map((commune) => (
              <Link
                key={commune.slug}
                to={commune.urlPath}
                className="flex items-center gap-2 px-4 py-3 bg-white border border-background-200 rounded-xl hover:border-accent-300 hover:bg-accent-50 transition-all duration-200 text-sm font-medium text-foreground-600 hover:text-accent-500"
              >
                <MapPin className="w-4 h-4 shrink-0" />
                {commune.name}
              </Link>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-12 text-center">
            <p className="text-base text-foreground-500 mb-4">
              Votre commune n'est pas dans la liste ? Contactez-nous, nous intervenons probablement aussi chez vous.
            </p>
            <a
              href="tel:+3280020410"
              className="btn-primary text-base px-8 py-3.5 inline-flex items-center gap-2"
            >
              <Phone className="w-5 h-5" />
              0800 20 410
            </a>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}